import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { DragDropData } from '../../../models/drag-drop-data';
import { Endpoint, HelpIndex } from '../../../models/enums';
import { FilterTemplateType, GenericColDef, GridContext, AlignDirection } from '../../../models/grid-context';
import { ToggleItem } from '../../../models/toggle-item';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { XMLService } from '../../../services/xml/xml.service';
import { LeftRightSelectionBoxComponent } from '../../left-right-selection-box/left-right-selection-box.component';
import { TargetPickerTreeType } from '../../target-picker-tree/target-picker-tree.component';
import { TargetPickerModalComponent } from '../target-picker-modal/target-picker-modal.component';
import { DownloadWordListModalComponent } from '../download-word-list-modal/download-word-list-modal.component';
import { Permissions, ObjectActionListType } from '../../../models/permission';
import { WorkflowStep } from '../../../models/workflow';

@Component({
    standalone: false,
    selector: 'res-word-list-modal',
    templateUrl: './word-list-modal.component.html',
    styleUrls: ['./word-list-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class WordListModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(LeftRightSelectionBoxComponent, { static: true }) leftRightSelectionBoxComponent: LeftRightSelectionBoxComponent;

    modalAutomationId = 'word-list';
    modalClass = 'word-list-modal';
    modalHelpIndex = HelpIndex.myedisc_word_list_report_dlg;
    itemsLeft = new Array<DragDropData>();
    itemsRight = new Array<DragDropData>();
    searchTarget = { name: 'Project Data', handle: this.commonService.projectData.handle };

    isBusyWordlist: boolean;
    isBusyMessage: string;
    wordsFilter: string;
    viewHandle: string;
    totalItems: number;
    corporaFilters: Array<any> = [];
    corporaFilterTooltip = '';
    hasDocReportsPermission = Permissions.checkUserPermission(ObjectActionListType.DOCUMENT_REPORTS);

    leftListColumns: Array<GenericColDef> = new Array<GenericColDef>(
        {
            colId: 'checkbox', automationId: this.modalAutomationId,
            checkboxSelection: true, headerCheckboxSelection: true,
            minWidth: 36, maxWidth: 36, width: 36,
            suppressSizeToFit: true, suppressMovable: true
        },
        {
            headerName: 'Word', field: 'word', sortBy: 'word', automationId: this.modalAutomationId, filterType: FilterTemplateType.freeform,
            suppressMovable: true, filterPlaceholder: 'Filter (wildcards accepted) ...'
        },
        {
            headerName: 'Occurrences', field: 'occurrence', sortBy: 'occur', automationId: this.modalAutomationId,
            headerAlignDirection: AlignDirection.right, cellAlignDirection: AlignDirection.right, width: 100
        },
        {
            headerName: 'Documents', field: 'docs', sortBy: 'docs', automationId: this.modalAutomationId,
            headerAlignDirection: AlignDirection.right, cellAlignDirection: AlignDirection.right, width: 100
        }
    );
    rightListColumns: Array<GenericColDef> = new Array<GenericColDef>(
        {
            colId: 'checkbox', automationId: this.modalAutomationId,
            checkboxSelection: true, headerCheckboxSelection: true,
            minWidth: 36, maxWidth: 36, width: 36,
            suppressSizeToFit: true, suppressMovable: true
        },
        {
            headerName: 'Word', field: 'word', automationId: this.modalAutomationId
        });

    leftListGridContext = new GridContext({
        inlineGridFilter: true, sortBy: 'docs', sortByFieldName: 'sortField', sortAscendingFieldName: 'sortDescending', sortAscending: false,
        onFilterApply: (queryStr: string) => {
            this.itemsLeft.length = 0;
            this.wordsFilter = queryStr ? queryStr : null;
            this.wordListRequestInfo.data.wordListFilter = this.wordsFilter;
            this.wordListRequestInfo.data.startIndex = 0;
            this.leftRightSelectionBoxComponent.genericGrids.first.loadData();
        }
    });
    rightListGridContext = new GridContext({});

    wordListRequestInfo: DrRequestInfo;

    searchToggles = [
        new ToggleItem({ label: 'Include Families', isChecked: true }),
        new ToggleItem({ label: 'Include Metadata', isChecked: true }),
        new ToggleItem({ label: 'Expand Synonyms' })
    ];

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private commonService: CommonService,
        private restService: RestService,
        private xmlService: XMLService
    ) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        if (this.modalData && this.modalData.handle) {
            this.viewHandle = this.modalData.handle;
            this.wordListRequestInfo = new DrRequestInfo({
                data: {
                    count: 100,
                    createIfMissing: false,
                    sortDescending: false,
                    sortField: null,
                    startIndex: 0,
                    viewHandle: this.viewHandle,
                    wordListFilter: null
                },
                endpoint: Endpoint.viewManager_getWordListPage,
                responseName: 'words'
            });
        }
    }

    responseArrayCallback = (value: any): DragDropData => {
        const item = new DragDropData(value);
        item.name = value.word;
        this.itemsLeft.push(item);
        return item;
    }

    responseCallback = (response: any): void => {
        this.itemsLeft.length = 0;
        if (response && response.numFilteredWords) {
            if (response.numFilteredWords >= 0) {
                this.totalItems = response.numFilteredWords;
            } else {
                // need this because numFilteredWords comes as -1 when calling getWordListPage for other page than the first one
                response.numFilteredWords = this.totalItems;
            }
        }
    }

    isSearchDisabled(): boolean {
        return this.itemsRight.length === 0 || this.searchTarget.handle === '';
    }

    leftListSelectedItems(): string {
        if (this.leftRightSelectionBoxComponent && this.leftRightSelectionBoxComponent.genericGrids && this.leftRightSelectionBoxComponent.genericGrids.first) {
            const count = this.leftRightSelectionBoxComponent.genericGrids.first.selectedRows.length;
            if (count > 0) {
                return `${count.toLocaleString()} on this page`;
            }
            return `${count.toLocaleString()}`;
        }
        return '0';
    }

    onTargetSelect(): void {
        this.commonService.openModal(TargetPickerModalComponent, {
            modalData: { targetPickerTreeType: TargetPickerTreeType.selectSearchTarget, dataSetFilter: this.corporaFilters },
            class: 'horizontal-px-700 vertical-percent-80'
        }).then(response => {
            if (response && response.modalResult && response.modalData && response.modalData.target) {
                this.searchTarget = response.modalData.target.data;
                this.corporaFilters = response.modalData.dataSetFilter;
                if (this.searchTarget.name) {
                    this.searchTarget.name = this.searchTarget.name.replace('CaseData', 'Project Data');
                }
                if (this.searchTarget instanceof WorkflowStep) {
                    response.modalData.target.data.name = response.modalData.target.data.description;
                }
                if (this.corporaFilters && this.corporaFilters.length > 0) {
                    const names = this.corporaFilters.map(function (elem) { return '- ' + elem.name; });
                    this.corporaFilterTooltip = 'Filtered by Data Set/s:\n' + names.join('\n');
                } else {
                    this.corporaFilterTooltip = '';
                }
            }
        });
    }

    search(): void {
        if (this.itemsRight && this.itemsRight.length > 0) {
            const query = this.itemsRight.map(item => {
                let temp = item.name;
                if ((item.name.includes('/') || item.name.includes('\\'))) {
                    if (item.name.includes('\\')) {
                        temp = temp.replace(/\\/g, '\\\\');
                    }
                    temp = '\u0027' + temp + '\u0027';
                }
                return temp;
            }).join(' OR ');
            const xmlQuery = this.xmlService.stringToXMLQuery(query);
            this.isBusyWordlist = true;
            this.isBusyMessage = 'Searching ...';
            const request = {
                viewHandleList: [this.searchTarget.handle],
                query: xmlQuery,
                includeFamilyMembers: this.searchToggles[0].isChecked,
                expandFields: this.searchToggles[1].isChecked,
                includeSynonyms: this.searchToggles[2].isChecked,
                taskDescription: '(' + query + ') in ' + this.searchTarget.name,
                attributes: [{
                    name: 'searchDialect',
                    value: this.commonService.searchDialect
                }],
                reportRequests: [],
                corporaFilters: this.corporaFilters ? this.corporaFilters.map(function (elem) { return elem.handle; }) : []
            };

            this.restService.post(Endpoint.viewManager_search, request).then(response => {
                this.isBusyWordlist = false;
            }, () => {
                this.isBusyWordlist = false;
            });
        }
    }

    downloadWordList(): void {
        this.commonService.openModal(DownloadWordListModalComponent, {
            modalData: {
                sourceViewHandle: this.viewHandle, wordListFilter: this.wordsFilter,
                sortField: this.wordListRequestInfo.data.sortField, sortDescending: this.wordListRequestInfo.data.sortDescending
            }
        });
    }

    // 'add' - copy items from left list to right list, 'remove' - remove items from right
    copyOrRemoveItems(data: any): void {
        if (data && data.mode && data.items) {
            data.items.forEach(item => {
                const sourceArray = data.mode === 'add' ? this.itemsLeft : this.itemsRight;
                const targetArray = data.mode === 'add' ? this.itemsRight : this.itemsLeft;
                if (item instanceof DragDropData) {
                    if (data.mode === 'add') {
                        const newItemIdx = sourceArray.indexOf(item);
                        if (newItemIdx > -1 && targetArray.findIndex(e => e === sourceArray[newItemIdx]) === -1) {
                            targetArray.push(sourceArray[newItemIdx]);
                        }
                    } else {
                        sourceArray.splice(sourceArray.indexOf(item), 1).pop();
                    }
                }
                if (data.mode === 'add' && this.leftRightSelectionBoxComponent.sortItemsRight) {
                    this.leftRightSelectionBoxComponent.sortList(targetArray);
                }
            });
            this.leftRightSelectionBoxComponent.setLeftItems(this.itemsLeft);
            this.leftRightSelectionBoxComponent.setRightItems(this.itemsRight);
            if (this.leftRightSelectionBoxComponent.genericGrids && this.leftRightSelectionBoxComponent.genericGrids.first && this.leftRightSelectionBoxComponent.genericGrids.first.allRowsSelected) {
                this.leftRightSelectionBoxComponent.genericGrids.first.gridApi.deselectAll();
            }
        }
    }
}
